import { createServerFn } from "@tanstack/react-start";
import { createClient } from "@supabase/supabase-js";
import { z } from "zod";
import { requireSupabaseAuth } from "@/integrations/supabase/auth-middleware";
import type { Database } from "@/integrations/supabase/types";

export type PublicEvent = {
  id: string;
  slug: string;
  title: string;
  description: string;
  short_description: string | null;
  host: string;
  category: string;
  event_image_url: string | null;
  starts_at: string;
  duration_minutes: number;
  registration_limit: number | null;
  reservation_count: number;
  status: string;
  source: "internal" | "external";
  external_url: string | null;
  location: string | null;
};

export type MemberEvent = PublicEvent & {
  zoom_url: string | null;
  zoom_passcode: string | null;
};

export type AdminEvent = MemberEvent & {
  created_at: string;
  updated_at: string;
};


type ZoomLinkRow = { event_id: string; zoom_url: string | null; zoom_passcode: string | null };

function publicClient() {
  return createClient<Database>(
    process.env.SUPABASE_URL!,
    process.env.SUPABASE_PUBLISHABLE_KEY!,
    { auth: { storage: undefined, persistSession: false, autoRefreshToken: false } },
  );
}

const EVENT_BASE_COLS =
  "id, slug, title, description, short_description, host, category, event_image_url, starts_at, duration_minutes, registration_limit, status, source, external_url, location";


/**
 * Aggregate reservation counts per event. Uses the service-role client so
 * the counts are visible even to signed-out visitors (aggregate only —
 * never leaks reservation identities).
 */
async function fetchReservationCounts(eventIds: string[]): Promise<Record<string, number>> {
  const out: Record<string, number> = {};
  if (!eventIds.length) return out;
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data } = await supabaseAdmin
    .from("event_reservations")
    .select("event_id")
    .in("event_id", eventIds);
  for (const r of ((data ?? []) as Array<{ event_id: string }>)) {
    out[r.event_id] = (out[r.event_id] ?? 0) + 1;
  }
  return out;
}

// ---------- Public reads (no Zoom link) ----------

export const listPublishedEvents = createServerFn({ method: "GET" }).handler(
  async (): Promise<PublicEvent[]> => {
    try {
      const sb = publicClient();
      const { data, error } = await sb
        .from("events_public" as never)
        .select("*")
        .order("starts_at", { ascending: true });
      if (error) throw new Error(error.message);
      const rows = (data ?? []) as unknown as Array<Omit<PublicEvent, "reservation_count">>;
      const counts = await fetchReservationCounts(rows.map((r) => r.id));
      return rows.map((r) => ({ ...r, reservation_count: counts[r.id] ?? 0 }));
    } catch (err) {
      // Backend unreachable (e.g. Cloudflare 521) — degrade to an empty list
      // rather than throwing raw HTML into the page and blanking it.
      console.error("[listPublishedEvents] falling back to empty list", err);
      return [];
    }
  },
);

export const getPublicEvent = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => z.object({ slug: z.string().min(1) }).parse(d))
  .handler(async ({ data }): Promise<PublicEvent | null> => {
    try {
      const sb = publicClient();
      const { data: row, error } = await sb
        .from("events_public" as never)
        .select("*")
        .eq("slug", data.slug)
        .maybeSingle();
      if (error) throw new Error(error.message);
      if (!row) return null;
      const base = row as unknown as Omit<PublicEvent, "reservation_count">;
      const counts = await fetchReservationCounts([base.id]);
      return { ...base, reservation_count: counts[base.id] ?? 0 };
    } catch (err) {
      console.error("[getPublicEvent] backend unreachable", err);
      return null;
    }
  });



// ---------- Authenticated member reads (with Zoom link if reserved) ----------

async function fetchZoomLinks(
  client: ReturnType<typeof publicClient>,
  eventIds: string[],
): Promise<Map<string, { zoom_url: string | null; zoom_passcode: string | null }>> {
  const map = new Map<string, { zoom_url: string | null; zoom_passcode: string | null }>();
  if (!eventIds.length) return map;
  const { data } = await client
    .from("event_zoom_links" as never)
    .select("event_id, zoom_url, zoom_passcode")
    .in("event_id", eventIds);
  for (const row of ((data ?? []) as unknown as ZoomLinkRow[])) {
    map.set(row.event_id, { zoom_url: row.zoom_url, zoom_passcode: row.zoom_passcode });
  }
  return map;
}

export const getEventForMember = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { slug: string }) => z.object({ slug: z.string().min(1) }).parse(d))
  .handler(async ({ data, context }): Promise<MemberEvent | null> => {
    const { data: row, error } = await context.supabase
      .from("events")
      .select(EVENT_BASE_COLS)
      .eq("slug", data.slug)
      .eq("status", "published")
      .maybeSingle();
    if (error) throw new Error(error.message);
    if (!row) return null;
    const base = row as unknown as Omit<PublicEvent, "reservation_count">;
    const zoomMap = await fetchZoomLinks(context.supabase as never, [base.id]);
    const zoom = zoomMap.get(base.id) ?? { zoom_url: null, zoom_passcode: null };
    const counts = await fetchReservationCounts([base.id]);
    return { ...base, ...zoom, reservation_count: counts[base.id] ?? 0 };
  });

export const listEventsForMember = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<MemberEvent[]> => {
    const { data, error } = await context.supabase
      .from("events")
      .select(EVENT_BASE_COLS)
      .eq("status", "published")
      .order("starts_at", { ascending: true });
    if (error) throw new Error(error.message);
    const rows = (data ?? []) as unknown as Array<Omit<PublicEvent, "reservation_count">>;
    const zoomMap = await fetchZoomLinks(context.supabase as never, rows.map((r) => r.id));
    const counts = await fetchReservationCounts(rows.map((r) => r.id));
    return rows.map((r) => ({
      ...r,
      ...(zoomMap.get(r.id) ?? { zoom_url: null, zoom_passcode: null }),
      reservation_count: counts[r.id] ?? 0,
    }));
  });


// ---------- Reservations ----------

/**
 * Server-authoritative access check for event reservation.
 * `is_paid_or_admin` covers Stripe, PayPal, complimentary/founding
 * memberships and admins, so the UI never has to guess from client state.
 */
export const canIReserveEvents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<boolean> => {
    const { data, error } = await context.supabase.rpc("is_paid_or_admin", {
      _user_id: context.userId,
    });
    if (error) throw new Error(error.message);
    return !!data;
  });



export const listMyReservations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<string[]> => {
    const { data, error } = await context.supabase
      .from("event_reservations")
      .select("event_id")
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);
    return (data ?? []).map((r: { event_id: string }) => r.event_id);
  });

export const reserveEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { eventId: string; timeZone?: string }) =>
    z.object({ eventId: z.string().uuid(), timeZone: z.string().max(64).optional() }).parse(d),
  )
  .handler(async ({ data, context }) => {
    // Fetch event for email content and capacity. Events without a
    // registration_limit stay unlimited; with a limit, members who arrive
    // after the last place joins a waiting list instead.
    const { data: evRow, error: evErr } = await context.supabase
      .from("events")
      .select("id, slug, title, host, starts_at, duration_minutes, status, registration_limit")
      .eq("id", data.eventId)
      .maybeSingle();
    if (evErr) throw new Error(evErr.message);
    const ev = evRow as unknown as {
      id: string;
      slug: string;
      title: string;
      host: string;
      starts_at: string;
      duration_minutes: number;
      status: string;
      registration_limit: number | null;
    } | null;
    if (!ev || ev.status !== "published") throw new Error("Event is not available");

    // Past events can no longer be booked. We allow a small grace window so a
    // session that has just started is still joinable.
    const endsAt =
      new Date(ev.starts_at).getTime() + (ev.duration_minutes ?? 60) * 60_000;
    if (Number.isFinite(endsAt) && endsAt <= Date.now()) {
      throw new Error("This event has already taken place and can no longer be booked.");
    }


    // Membership is enforced server-side (and again by RLS on insert): a
    // signed-in but unpaid account cannot reserve by calling this directly.
    const { data: allowed, error: accessErr } = await context.supabase.rpc("is_paid_or_admin", {
      _user_id: context.userId,
    });
    if (accessErr) throw new Error(accessErr.message);
    if (!allowed) throw new Error("An active Recovery Trauma\u2122 membership is required to reserve a place.");


    // Member's email + display name from auth
    const email = (context.claims as { email?: string })?.email ?? null;
    const displayName =
      (context.claims as { user_metadata?: { display_name?: string; full_name?: string; name?: string } })
        ?.user_metadata;
    const name =
      displayName?.display_name || displayName?.full_name || displayName?.name || null;

    // Capacity: count confirmed places with the service-role client so the
    // count is complete (RLS limits a member to their own reservation rows).
    let waitlisted = false;
    const limit = ev.registration_limit ?? null;
    if (limit != null && limit > 0) {
      const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
      const { count } = await supabaseAdmin
        .from("event_reservations")
        .select("id", { count: "exact", head: true })
        .eq("event_id", ev.id)
        .eq("waitlisted", false);
      waitlisted = (count ?? 0) >= limit;
    }

    const { error: insErr } = await context.supabase.from("event_reservations").insert({
      event_id: data.eventId,
      user_id: context.userId,
      email,
      display_name: name,
      waitlisted,
    });
    if (insErr && !insErr.message.includes("duplicate")) throw new Error(insErr.message);

    // Remember the member's time zone so reminders can show their local time.
    if (data.timeZone) {
      await context.supabase
        .from("profiles")
        .update({ time_zone: data.timeZone } as never)
        .eq("id", context.userId);
    }

    // Fire confirmation email (best-effort). Read zoom link via service-role
    // client because the user just reserved and the email needs the link.
    // Waiting-list places get no joining details until a place opens.
    if (email && !waitlisted) {
      try {
        const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
        const { data: zoomRow } = await supabaseAdmin
          .from("event_zoom_links" as never)
          .select("zoom_url, zoom_passcode")
          .eq("event_id", ev.id)
          .maybeSingle();
        const zoom = (zoomRow as unknown as { zoom_url: string | null; zoom_passcode: string | null } | null) ?? {
          zoom_url: null,
          zoom_passcode: null,
        };
        const { sendReservationConfirmation } = await import("@/lib/events.server");
        await sendReservationConfirmation(email, {
          slug: ev.slug,
          title: ev.title,
          host: ev.host,
          starts_at: ev.starts_at,
          duration_minutes: ev.duration_minutes,
          zoom_url: zoom.zoom_url,
          zoom_passcode: zoom.zoom_passcode,
        }, data.timeZone ?? null);
      } catch (e) {
        console.error("[reserveEvent] confirmation email failed", e);
      }
    }
    return { ok: true, waitlisted };
  });

export const cancelReservation = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { eventId: string }) => z.object({ eventId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    // Remember whether this was a confirmed place — if it was, the first
    // person waiting moves up.
    const { data: mine } = await context.supabase
      .from("event_reservations")
      .select("waitlisted")
      .eq("event_id", data.eventId)
      .eq("user_id", context.userId)
      .maybeSingle();
    const freedConfirmedPlace = mine ? (mine as { waitlisted: boolean }).waitlisted === false : false;

    const { error } = await context.supabase
      .from("event_reservations")
      .delete()
      .eq("event_id", data.eventId)
      .eq("user_id", context.userId);
    if (error) throw new Error(error.message);

    if (freedConfirmedPlace) {
      try {
        await promoteFromWaitlist(data.eventId);
      } catch (e) {
        console.error("[cancelReservation] waiting-list promotion failed", e);
      }
    }
    return { ok: true };
  });

/**
 * Move the longest-waiting member into a freed place (service-role, so it can
 * see and update other members' rows) and email them the joining details.
 */
async function promoteFromWaitlist(eventId: string): Promise<void> {
  const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
  const { data: evRow } = await supabaseAdmin
    .from("events")
    .select("id, slug, title, host, starts_at, duration_minutes, status, registration_limit")
    .eq("id", eventId)
    .maybeSingle();
  const ev = evRow as unknown as {
    id: string;
    slug: string;
    title: string;
    host: string;
    starts_at: string;
    duration_minutes: number;
    status: string;
    registration_limit: number | null;
  } | null;
  if (!ev || ev.status !== "published" || !ev.registration_limit) return;

  const { count } = await supabaseAdmin
    .from("event_reservations")
    .select("id", { count: "exact", head: true })
    .eq("event_id", eventId)
    .eq("waitlisted", false);
  if ((count ?? 0) >= ev.registration_limit) return;

  const { data: nextRow } = await supabaseAdmin
    .from("event_reservations")
    .select("id, email")
    .eq("event_id", eventId)
    .eq("waitlisted", true)
    .order("reserved_at", { ascending: true })
    .limit(1)
    .maybeSingle();
  const next = nextRow as unknown as { id: string; email: string | null } | null;
  if (!next) return;

  await supabaseAdmin
    .from("event_reservations")
    .update({ waitlisted: false, promoted_at: new Date().toISOString() } as never)
    .eq("id", next.id);

  if (!next.email) return;
  const { data: zoomRow } = await supabaseAdmin
    .from("event_zoom_links" as never)
    .select("zoom_url, zoom_passcode")
    .eq("event_id", eventId)
    .maybeSingle();
  const zoom = (zoomRow as unknown as { zoom_url: string | null; zoom_passcode: string | null } | null) ?? {
    zoom_url: null,
    zoom_passcode: null,
  };
  const { sendReservationConfirmation } = await import("@/lib/events.server");
  await sendReservationConfirmation(next.email, {
    slug: ev.slug,
    title: ev.title,
    host: ev.host,
    starts_at: ev.starts_at,
    duration_minutes: ev.duration_minutes,
    zoom_url: zoom.zoom_url,
    zoom_passcode: zoom.zoom_passcode,
  }, null);
}

/** Public sign-up summary: places taken, places left, waiting-list size. */
export const getEventSignupSummary = createServerFn({ method: "GET" })
  .inputValidator((d: { slug: string }) => z.object({ slug: z.string().min(1).max(160) }).parse(d))
  .handler(async ({ data }) => {
    const { supabaseAdmin } = await import("@/integrations/supabase/client.server");
    const { data: evRow } = await supabaseAdmin
      .from("events")
      .select("id, registration_limit, status")
      .eq("slug", data.slug)
      .maybeSingle();
    const ev = evRow as unknown as { id: string; registration_limit: number | null; status: string } | null;
    if (!ev || ev.status !== "published") {
      return { confirmed: 0, waiting: 0, limit: null as number | null, placesLeft: null as number | null };
    }
    const [{ count: confirmed }, { count: waiting }] = await Promise.all([
      supabaseAdmin
        .from("event_reservations")
        .select("id", { count: "exact", head: true })
        .eq("event_id", ev.id)
        .eq("waitlisted", false),
      supabaseAdmin
        .from("event_reservations")
        .select("id", { count: "exact", head: true })
        .eq("event_id", ev.id)
        .eq("waitlisted", true),
    ]);
    const limit = ev.registration_limit ?? null;
    return {
      confirmed: confirmed ?? 0,
      waiting: waiting ?? 0,
      limit,
      placesLeft: limit == null ? null : Math.max(0, limit - (confirmed ?? 0)),
    };
  });

/** The signed-in member's own place: none, confirmed or waiting. */
export const getMyEventPlace = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { eventId: string }) => z.object({ eventId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }): Promise<"none" | "confirmed" | "waiting"> => {
    const { data: row } = await context.supabase
      .from("event_reservations")
      .select("waitlisted")
      .eq("event_id", data.eventId)
      .eq("user_id", context.userId)
      .maybeSingle();
    if (!row) return "none";
    return (row as { waitlisted: boolean }).waitlisted ? "waiting" : "confirmed";
  });

// ---------- Admin ----------

async function requireAdmin(context: { supabase: ReturnType<typeof publicClient>; userId: string }) {
  const { data, error } = await context.supabase.rpc("has_role", {
    _user_id: context.userId,
    _role: "admin",
  });
  if (error) throw new Error(error.message);
  if (!data) throw new Error("Forbidden");
}

export const adminListEvents = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .handler(async ({ context }): Promise<AdminEvent[]> => {
    await requireAdmin(context as never);
    const { data, error } = await context.supabase
      .from("events")
      .select("*")
      .order("starts_at", { ascending: false });
    if (error) throw new Error(error.message);

    const rows = (data ?? []) as unknown as Array<Record<string, unknown> & { id: string }>;
    const ids = rows.map((e) => e.id);
    let counts: Record<string, number> = {};
    if (ids.length) {
      const { data: rs } = await context.supabase
        .from("event_reservations")
        .select("event_id")
        .in("event_id", ids);
      counts = (rs ?? []).reduce<Record<string, number>>((acc, r: { event_id: string }) => {
        acc[r.event_id] = (acc[r.event_id] || 0) + 1;
        return acc;
      }, {});
    }
    const zoomMap = await fetchZoomLinks(context.supabase as never, ids);
    return rows.map((e) => ({
      ...(e as unknown as PublicEvent),
      ...(zoomMap.get(e.id) ?? { zoom_url: null, zoom_passcode: null }),
      created_at: e.created_at as string,
      updated_at: e.updated_at as string,
      reservation_count: counts[e.id] || 0,
    }));
  });

const EventInputSchema = z.object({
  id: z.string().uuid().optional(),
  slug: z.string().min(1).max(120).regex(/^[a-z0-9-]+$/, "Lowercase letters, numbers, dashes only"),
  title: z.string().min(1).max(200),
  description: z.string().max(5000).default(""),
  host: z.string().max(200).default(""),
  category: z.string().max(60).default("community"),
  event_image_url: z.string().url().nullable().optional(),
  starts_at: z.string().min(1),
  duration_minutes: z.number().int().positive().max(600),
  zoom_url: z.string().url().nullable().optional(),
  zoom_passcode: z.string().max(60).nullable().optional(),
  registration_limit: z.number().int().positive().max(100).nullable().optional(),
  status: z.enum(["draft", "published", "completed"]),
  source: z.enum(["internal", "external"]).default("internal"),
  external_url: z.string().url().nullable().optional(),
  location: z.string().max(200).nullable().optional(),
});

async function upsertZoomLink(
  client: ReturnType<typeof publicClient>,
  eventId: string,
  zoomUrl: string | null,
  zoomPasscode: string | null,
) {
  if (!zoomUrl && !zoomPasscode) {
    await client.from("event_zoom_links" as never).delete().eq("event_id", eventId);
    return;
  }
  const { error } = await client
    .from("event_zoom_links" as never)
    .upsert(
      { event_id: eventId, zoom_url: zoomUrl, zoom_passcode: zoomPasscode, updated_at: new Date().toISOString() } as never,
      { onConflict: "event_id" },
    );
  if (error) throw new Error(error.message);
}

export const adminUpsertEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: unknown) => EventInputSchema.parse(d))
  .handler(async ({ data, context }) => {
    await requireAdmin(context as never);
    const payload = {
      slug: data.slug,
      title: data.title,
      description: data.description,
      host: data.host,
      category: data.category,
      event_image_url: data.event_image_url ?? null,
      starts_at: data.starts_at,
      duration_minutes: data.duration_minutes,
      registration_limit: data.registration_limit ?? null,
      status: data.status,
      source: data.source,
      external_url: data.external_url ?? null,
      location: data.location ?? null,
    };

    let eventId: string;
    if (data.id) {
      const { error } = await context.supabase
        .from("events")
        .update(payload as never)
        .eq("id", data.id);
      if (error) throw new Error(error.message);
      eventId = data.id;
    } else {
      const { data: row, error } = await context.supabase
        .from("events")
        .insert({ ...payload, created_by: context.userId } as never)
        .select("id")
        .single();
      if (error) throw new Error(error.message);
      eventId = (row as unknown as { id: string }).id;
    }
    await upsertZoomLink(
      context.supabase as never,
      eventId,
      data.zoom_url ?? null,
      data.zoom_passcode ?? null,
    );
    return { ok: true, id: eventId };
  });

export const adminDeleteEvent = createServerFn({ method: "POST" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { id: string }) => z.object({ id: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }) => {
    await requireAdmin(context as never);
    const { error } = await context.supabase.from("events").delete().eq("id", data.id);
    if (error) throw new Error(error.message);
    return { ok: true };
  });

export type AdminReservation = {
  id: string;
  event_id: string;
  user_id: string;
  display_name: string | null;
  email: string | null;
  reserved_at: string;
};

export const adminListReservations = createServerFn({ method: "GET" })
  .middleware([requireSupabaseAuth])
  .inputValidator((d: { eventId: string }) => z.object({ eventId: z.string().uuid() }).parse(d))
  .handler(async ({ data, context }): Promise<AdminReservation[]> => {
    await requireAdmin(context as never);
    const { data: rows, error } = await context.supabase
      .from("event_reservations")
      .select("id, event_id, user_id, display_name, email, reserved_at")
      .eq("event_id", data.eventId)
      .order("reserved_at", { ascending: false });
    if (error) throw new Error(error.message);
    return (rows ?? []) as AdminReservation[];
  });
